/**
 * EnquiryBand — a visitor leaves their number and admissions calls them back.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, PhoneCall } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EnquiryBand = ({ tenantId, programmes = [], brand }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', programme_id: '', best_time: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/public/school/tenants/${tenantId}/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // FastAPI validation errors arrive as an array of {loc, msg} objects.
        const detail = Array.isArray(body.detail)
          ? body.detail.map((d) => d.msg || '').filter(Boolean).join('. ')
          : body.detail;
        throw new Error(
          typeof detail === 'string' && detail
            ? detail
            : 'Please check your name and phone number and try again');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <section id="enquire" data-testid="school-enquiry-section"
             className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase mb-3" style={{ color: brand }}>
            No pressure
          </p>
          <h2 className="fw-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Rather have us call you?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Leave your number and the admissions office will ring you back to answer anything —
            fees, entry requirements, boarding, start dates.
          </p>
        </div>

        <div className="lg:col-span-7 w-full">
          {done ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-8 py-10 text-center"
                 data-testid="enquiry-success">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600" />
              <p className="mt-4 font-semibold text-emerald-900">Thank you, {form.name.split(' ')[0]}</p>
              <p className="mt-1 text-sm text-emerald-800">
                The admissions office has your number and will call you back.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7 space-y-4"
                  data-testid="enquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="enq-name">Your name *</Label>
                  <Input id="enq-name" required value={form.name} onChange={set('name')}
                         data-testid="enquiry-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enq-phone">Phone number *</Label>
                  <Input id="enq-phone" required value={form.phone} onChange={set('phone')}
                         data-testid="enquiry-phone" placeholder="0771234567" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enq-email">Email</Label>
                  <Input id="enq-email" type="email" value={form.email} onChange={set('email')}
                         data-testid="enquiry-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enq-time">Best time to call</Label>
                  <Input id="enq-time" value={form.best_time} onChange={set('best_time')}
                         data-testid="enquiry-best-time" placeholder="Mornings" />
                </div>
                {programmes.length > 0 && (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="enq-prog">Interested in</Label>
                    <select id="enq-prog" value={form.programme_id} onChange={set('programme_id')}
                            data-testid="enquiry-programme"
                            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                      <option value="">Not sure yet</option>
                      {programmes.filter((p) => p.id).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="enq-msg">Anything you'd like to ask?</Label>
                  <textarea id="enq-msg" rows={3} value={form.message} onChange={set('message')}
                            data-testid="enquiry-message"
                            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                </div>
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="enquiry-error">{error}</p>}
              <Button type="submit" size="lg" disabled={busy} data-testid="enquiry-submit"
                      className="rounded-full px-8 font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
                      style={{ backgroundColor: brand }}>
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <PhoneCall className="h-4 w-4 mr-2" />}
                Request a call back
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default EnquiryBand;
